import React, { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Megaphone, Trash2, Radio, Play, Pause, RefreshCw } from "lucide-react";
import { api, resolveAudio } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/**
 * Owner-only self-serve ad launcher.
 * Bypasses Stripe — admin uploads an MP3 (+ optional cover) and the ad goes
 * live instantly on the standard rotation across every station.
 * Ideal for the platform owner promoting their own brands, ebooks, or
 * cross-station shout-outs at zero cost.
 */
export default function OwnerAdLauncherCard() {
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [campaignLabel, setCampaignLabel] = useState("");
  const [clickUrl, setClickUrl] = useState("");
  const [days, setDays] = useState(30);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const [live, setLive] = useState([]);
  const [loadingLive, setLoadingLive] = useState(false);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);

  const loadLive = async () => {
    setLoadingLive(true);
    try {
      const { data } = await api.get(`/admin/ads?status=live`, { headers: authHeaders() });
      const mine = (data || []).filter(a => a.source === "self_launch");
      setLive(mine);
    } catch (_) { setLive([]); }
    finally { setLoadingLive(false); }
  };
  useEffect(() => { loadLive(); }, []);

  const submit = async () => {
    if (!audio) { setMsg("Pick an audio file first."); return; }
    if (!businessName.trim()) { setMsg("Add a brand / campaign name."); return; }
    setMsg(""); setUploading(true);
    const fd = new FormData();
    fd.append("audio", audio);
    if (cover) fd.append("cover", cover);
    fd.append("business_name", businessName.trim());
    fd.append("campaign_label", campaignLabel.trim());
    fd.append("days", String(days || 30));
    if (clickUrl.trim()) fd.append("click_url", clickUrl.trim());
    try {
      const { data } = await api.post("/admin/ads/self-launch", fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setMsg(`✅ Live: “${data.ad.business_name}” — ${Math.round(data.ad.audio_duration)}s ad, runs ${data.ad.days} days.`);
      setAudio(null); setCover(null); setBusinessName(""); setCampaignLabel(""); setClickUrl(""); setDays(30);
      loadLive();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Launch failed. Check the file and try again.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (orderId, name) => {
    if (!window.confirm(`Remove "${name}" from rotation? This action cannot be undone.`)) return;
    try {
      await api.delete(`/admin/ads/${orderId}`, { headers: authHeaders() });
      loadLive();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const togglePlay = (url, id) => {
    if (playing === id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const el = new Audio(resolveAudio(url));
    el.play().then(() => { audioRef.current = el; setPlaying(id); }).catch(() => {});
    el.onended = () => setPlaying(null);
  };

  return (
    <section className="fl-card p-6 mt-6" data-testid="owner-ad-launcher">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="fl-display text-xl font-bold text-white">Self-serve ad launcher</h2>
            <p className="text-white/50 text-sm mt-0.5">
              Promote your own brands, ebooks, or stations. Skips Stripe. Goes live instantly on every station&apos;s rotation.
            </p>
          </div>
        </div>
        <button onClick={loadLive} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="owner-ads-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loadingLive ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Upload form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Brand / campaign name *</span>
          <input
            className="fl-input mt-1 w-full"
            placeholder="Fliboe Ebook — Beat Making 101"
            value={businessName}
            onChange={e => setBusinessName(e.target.value)}
            data-testid="owner-ad-business-name"
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Internal label (optional)</span>
          <input
            className="fl-input mt-1 w-full"
            placeholder="Ebook launch — Q1"
            value={campaignLabel}
            onChange={e => setCampaignLabel(e.target.value)}
            data-testid="owner-ad-label"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Click-through URL (optional)</span>
          <input
            className="fl-input mt-1 w-full"
            placeholder="https://fliboe.com/ebooks/beat-making-101"
            value={clickUrl}
            onChange={e => setClickUrl(e.target.value)}
            data-testid="owner-ad-click-url"
          />
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Audio file (mp3/wav/m4a) *</span>
          <input
            type="file"
            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/aac,audio/*"
            onChange={e => setAudio(e.target.files?.[0] || null)}
            className="mt-1 block w-full text-sm text-white/70 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-emerald-500 file:text-black file:font-semibold hover:file:bg-emerald-400"
            data-testid="owner-ad-audio"
          />
          {audio && <div className="mt-1 text-xs text-white/50 truncate">{audio.name}</div>}
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Cover image (optional)</span>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={e => setCover(e.target.files?.[0] || null)}
            className="mt-1 block w-full text-sm text-white/70 file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-white/10 file:text-white file:font-semibold hover:file:bg-white/20"
            data-testid="owner-ad-cover"
          />
          {cover && <div className="mt-1 text-xs text-white/50 truncate">{cover.name}</div>}
        </label>

        <label className="block">
          <span className="text-xs uppercase tracking-widest text-white/50 font-semibold">Duration (days on air)</span>
          <input
            type="number"
            min={1}
            max={365}
            className="fl-input mt-1 w-full"
            value={days}
            onChange={e => setDays(Number(e.target.value) || 30)}
            data-testid="owner-ad-days"
          />
        </label>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          onClick={submit}
          disabled={uploading || !audio || !businessName.trim()}
          className="fl-btn"
          data-testid="owner-ad-launch-btn"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Launching…" : "Launch campaign"}
        </button>
        {msg && <div className="text-sm text-white/70" data-testid="owner-ad-msg">{msg}</div>}
      </div>

      {/* Currently-live owner ads */}
      <div className="mt-8">
        <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Your live campaigns</div>
        {loadingLive ? (
          <div className="text-white/40 text-sm">Loading…</div>
        ) : live.length === 0 ? (
          <div className="text-white/40 text-sm">No owner-launched ads are currently on air.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="owner-ads-live-list">
            {live.map(ad => (
              <div key={ad.order_id} className="fl-card p-4 flex items-center gap-3" data-testid={`owner-ad-${ad.order_id}`}>
                {ad.cover_url ? (
                  <img src={resolveAudio(ad.cover_url)} alt="" className="w-14 h-14 rounded-lg object-cover border border-white/10" />
                ) : (
                  <div className="w-14 h-14 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <Radio className="w-5 h-5 text-white/40" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-white font-semibold text-sm truncate">{ad.business_name}</div>
                  <div className="text-white/50 text-xs mt-0.5 truncate">
                    {Math.round(ad.audio_duration || 0)}s · {ad.play_count || 0} plays · ends {ad.expires_at ? new Date(ad.expires_at).toLocaleDateString() : "—"}
                  </div>
                  {ad.click_url && (
                    <a href={ad.click_url} target="_blank" rel="noreferrer" className="text-emerald-300 text-xs hover:underline truncate block mt-0.5">
                      {ad.click_url}
                    </a>
                  )}
                </div>
                <button
                  onClick={() => togglePlay(ad.audio_url, ad.order_id)}
                  className="fl-btn ghost !py-1.5 !px-2.5"
                  data-testid={`owner-ad-play-${ad.order_id}`}
                >
                  {playing === ad.order_id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => remove(ad.order_id, ad.business_name)}
                  className="fl-btn ghost !py-1.5 !px-2.5 hover:!border-red-400/40 hover:!text-red-300"
                  data-testid={`owner-ad-delete-${ad.order_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
